import { useEffect, type ReactNode } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * A centred dialog. Closes on Escape and on a backdrop click; renders nothing while closed.
 * On paper (`printDialog`) only its body prints: the title row and the footer's buttons drop out.
 */
export default function Modal({ open, onClose, title, children, footer }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-(--z-modal) flex animate-overlay-in items-center justify-center bg-black/70 p-3 sm:p-5"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[92svh] w-full max-w-[680px] animate-modal-in overflow-y-auto overscroll-contain rounded-3xl border border-hairline bg-surface p-5 shadow-lift sm:max-h-[88vh] sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="no-print mb-5 flex items-start justify-between gap-4 sm:mb-6 sm:gap-6">
          <h2 className="m-0 min-w-0 text-2xl leading-tight font-semibold tracking-tighter break-words sm:text-3xl">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close"
            className="-mt-1 inline-flex h-(--target-control) w-12 shrink-0 items-center justify-center rounded-xl border border-hairline text-2xl leading-none text-ink-muted transition-colors hover:border-brand-edge hover:text-brand-text focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
          >
            ×
          </button>
        </div>
        <div className="flex flex-col gap-5 text-base leading-relaxed text-ink-secondary sm:text-lg">
          {children}
        </div>
        {footer && (
          <div className="no-print mt-6 flex flex-wrap gap-3 max-sm:*:flex-1 sm:mt-8">{footer}</div>
        )}
      </div>
    </div>
  );
}
